/* ════════════════════════════════════════════════════════════════════════
   MEAL PLAN UTILS — week ordering, meal-slot management, nutrition totals,
   and one-time migration from the legacy weeklyMealPlan shape.
   Pure helpers — no state writes.
   ════════════════════════════════════════════════════════════════════════ */

export const DEFAULT_MEAL_SLOTS = [
  { id: "breakfast",    label: "Breakfast",     color: "yellow",   tile: "bg-yellow-50 dark:bg-yellow-900/15 border-yellow-200 dark:border-yellow-800/60",   text: "text-yellow-700 dark:text-yellow-300",   dot: "bg-yellow-500" },
  { id: "morningTea",   label: "Morning tea",   color: "peach",    tile: "bg-orange-50 dark:bg-orange-900/15 border-orange-200 dark:border-orange-800/60",   text: "text-orange-700 dark:text-orange-300",   dot: "bg-orange-400" },
  { id: "lunch",        label: "Lunch",         color: "green",    tile: "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800/60", text: "text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" },
  { id: "afternoonTea", label: "Afternoon tea", color: "sky",      tile: "bg-sky-50 dark:bg-sky-900/15 border-sky-200 dark:border-sky-800/60",               text: "text-sky-700 dark:text-sky-300",         dot: "bg-sky-500" },
  { id: "dinner",       label: "Dinner",        color: "coral",    tile: "bg-rose-50 dark:bg-rose-900/15 border-rose-200 dark:border-rose-800/60",           text: "text-rose-700 dark:text-rose-300",       dot: "bg-rose-500" },
  { id: "dessert",      label: "Dessert",       color: "lavender", tile: "bg-violet-50 dark:bg-violet-900/15 border-violet-200 dark:border-violet-800/60",   text: "text-violet-700 dark:text-violet-300",   dot: "bg-violet-500" },
];

export const DAY_ORDER_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const DAY_ORDER_LONG  = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SHORT_TO_LONG = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };
const LONG_TO_SHORT = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri", Saturday: "Sat", Sunday: "Sun" };

export function shortDay(d) { return LONG_TO_SHORT[d] || d; }
export function longDay(d)  { return SHORT_TO_LONG[d] || d; }

export const MEAL_STATUS_OPTIONS = ["Planned", "Needs decision", "Cooked", "Leftovers"];

export const SLOT_COLOR_OPTIONS = ["yellow", "peach", "green", "sky", "coral", "lavender", "rose", "amber", "indigo", "teal"];

/* ── Settings ─────────────────────────────────────────────────────── */

export function getMealPlanSettingsDefaults() {
  return {
    weekStartDay: "Fri", // Friday → Thursday order per spec
    mealSlots: DEFAULT_MEAL_SLOTS.map(s => ({ ...s })),
    showNutrition: true,
    showAssignedCook: true,
    showImages: true,
  };
}

/* Order the 7-day week from the chosen start day. */
export function getOrderedWeekDays(weekStartDay = "Fri") {
  const start = shortDay(weekStartDay);
  const startIdx = DAY_ORDER_SHORT.indexOf(start);
  if (startIdx < 0) return [...DAY_ORDER_SHORT];
  return [...DAY_ORDER_SHORT.slice(startIdx), ...DAY_ORDER_SHORT.slice(0, startIdx)];
}

export function getMealSlots(settings) {
  const list = settings?.mealSlots;
  return Array.isArray(list) && list.length ? list : DEFAULT_MEAL_SLOTS;
}

/* ── Migration — ensure each day has a slots map keyed by mealSlot id. ── */

function blankSlot() {
  return {
    title: "", recipeId: null, imageUrl: "", imageEmoji: "",
    assignedCook: null, status: "Planned",
    caloriesPerServe: null, macros: { protein: null, carbs: null, fat: null },
    servings: 1, notes: "",
  };
}

/* Map legacy day-level fields (breakfast/lunch/dinner strings) into the
   new flexible slots map. Idempotent — running it twice is safe. */
export function migrateDayToSlots(day, settings) {
  const slots = day.slots && typeof day.slots === "object" ? { ...day.slots } : {};
  const cfg = getMealSlots(settings);

  cfg.forEach(slot => {
    if (slots[slot.id]) return; // already migrated
    const legacy = day[slot.id]; // e.g. day.breakfast or day.lunch
    if (legacy && typeof legacy === "string") {
      slots[slot.id] = {
        ...blankSlot(),
        title: legacy,
        status: day.status || "Planned",
        assignedCook: slot.id === "dinner" ? (day.assignedCook || null) : null,
        recipeId: slot.id === "dinner" ? (day.dinnerRecipeId || null) : null,
      };
    } else {
      slots[slot.id] = blankSlot();
    }
  });
  return { ...day, slots };
}

export function migrateMealPlanToFlexibleSlots(plan = [], settings) {
  return (plan || []).map(d => migrateDayToSlots(d, settings));
}

/* ── Nutrition ────────────────────────────────────────────────────── */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* Resolve a slot's effective nutrition (per serve) using slot override
   first, then linked recipe. Returns null per field when unknown. */
export function calculateMealNutrition(slotData, recipe) {
  const slotKcal = num(slotData?.caloriesPerServe);
  const slotP    = num(slotData?.macros?.protein);
  const slotC    = num(slotData?.macros?.carbs);
  const slotF    = num(slotData?.macros?.fat);

  const recipeKcal = num(recipe?.caloriesPerServe);
  const recipeP    = num(recipe?.macros?.protein);
  const recipeC    = num(recipe?.macros?.carbs);
  const recipeF    = num(recipe?.macros?.fat);

  return {
    calories: slotKcal ?? recipeKcal,
    protein:  slotP    ?? recipeP,
    carbs:    slotC    ?? recipeC,
    fat:      slotF    ?? recipeF,
  };
}

const ZERO_TOTALS = () => ({ calories: 0, protein: 0, carbs: 0, fat: 0, mealsCounted: 0, mealsTotal: 0 });

/* Day totals from migrated slots map. */
export function calculateDayNutritionTotals(day, recipes = [], settings) {
  const totals = ZERO_TOTALS();
  const slotsCfg = getMealSlots(settings);
  slotsCfg.forEach(cfg => {
    const slot = day?.slots?.[cfg.id];
    if (!slot || (!slot.title && !slot.recipeId)) return;
    totals.mealsTotal += 1;
    const recipe = slot.recipeId ? recipes.find(r => r.id === slot.recipeId) : null;
    const n = calculateMealNutrition(slot, recipe);
    if (n.calories === null && n.protein === null && n.carbs === null && n.fat === null) return;
    totals.mealsCounted += 1;
    totals.calories += n.calories || 0;
    totals.protein  += n.protein || 0;
    totals.carbs    += n.carbs || 0;
    totals.fat      += n.fat || 0;
  });
  return totals;
}

/* Week totals across all migrated days. */
export function calculateWeekNutritionTotals(plan = [], recipes = [], settings) {
  return (plan || []).reduce((acc, day) => {
    const t = calculateDayNutritionTotals(day, recipes, settings);
    acc.calories += t.calories;
    acc.protein  += t.protein;
    acc.carbs    += t.carbs;
    acc.fat      += t.fat;
    acc.mealsCounted += t.mealsCounted;
    acc.mealsTotal   += t.mealsTotal;
    return acc;
  }, ZERO_TOTALS());
}

export function getNutritionCompleteness(day, recipes = [], settings) {
  const t = calculateDayNutritionTotals(day, recipes, settings);
  if (t.mealsTotal === 0) return { complete: true, label: "No meals planned" };
  if (t.mealsCounted === t.mealsTotal) return { complete: true, label: "All counted" };
  return { complete: false, label: `${t.mealsCounted}/${t.mealsTotal} meals calculated` };
}

/* ── Per-serve calculator (used in recipe nutrition section) ──────── */

export function calcRecipePerServe(total = {}, servings) {
  const n = Number(servings) || 0;
  if (n <= 0) return { caloriesPerServe: null, macros: { protein: null, carbs: null, fat: null } };
  return {
    caloriesPerServe: Number.isFinite(Number(total.calories)) ? +(Number(total.calories) / n).toFixed(0) : null,
    macros: {
      protein: Number.isFinite(Number(total.protein)) ? +(Number(total.protein) / n).toFixed(1) : null,
      carbs:   Number.isFinite(Number(total.carbs))   ? +(Number(total.carbs)   / n).toFixed(1) : null,
      fat:     Number.isFinite(Number(total.fat))     ? +(Number(total.fat)     / n).toFixed(1) : null,
    },
  };
}

/* Pretty formatting helpers. */
export function fmtKcal(v) { return v === null || v === undefined ? "—" : `${Math.round(v)} kcal`; }
export function fmtMacro(v) { return v === null || v === undefined ? "—" : `${Math.round(v * 10) / 10}g`; }

/* ── Today helpers ────────────────────────────────────────────────── */

export function getTodayDayShort() {
  // Sun=0 → match DAY_ORDER_SHORT
  const idx = (new Date().getDay() + 6) % 7; // Mon=0..Sun=6
  return DAY_ORDER_SHORT[idx];
}

export function getTodayMealPlanEntry(plan = [], todayShort = getTodayDayShort()) {
  return (plan || []).find(d => d.day === todayShort) || null;
}

/* ════════════════════════════════════════════════════════════════════════
   MEAL-SLOT MOVEMENT HELPERS — pure immutable operations on a meal plan
   array. Each function takes the current plan and returns a new plan.
   Used by drag-and-drop + the cell action menu.
   ════════════════════════════════════════════════════════════════════════ */

/* Read the slot data for a given day + slot. Returns blankSlot() shape if
   missing, never undefined — so callers can safely diff against "empty". */
export function getMealSlotByDayAndSlot(plan = [], day, slotId) {
  const dayObj = (plan || []).find(d => d.day === day);
  return dayObj?.slots?.[slotId] || blankSlot();
}

export function isSlotEmpty(slot) {
  return !slot || (!slot.title && !slot.recipeId);
}

/* Clone the user-facing fields of a slot so we never carry stale refs. */
function cloneSlot(slot) {
  const s = slot || blankSlot();
  return {
    title: s.title || "",
    recipeId: s.recipeId || null,
    imageUrl: s.imageUrl || "",
    imageEmoji: s.imageEmoji || "",
    assignedCook: s.assignedCook || null,
    status: s.status || "Planned",
    caloriesPerServe: s.caloriesPerServe ?? null,
    macros: {
      protein: s.macros?.protein ?? null,
      carbs:   s.macros?.carbs   ?? null,
      fat:     s.macros?.fat     ?? null,
    },
    servings: s.servings ?? 1,
    notes: s.notes || "",
  };
}

/* Replace a single (day, slotId) cell in the plan with the given slot
   object. Returns a new plan array. */
export function setMealSlotByDayAndSlot(plan = [], day, slotId, slotData) {
  return (plan || []).map(d => {
    if (d.day !== day) return d;
    const slots = { ...(d.slots || {}) };
    slots[slotId] = cloneSlot(slotData);
    return { ...d, slots };
  });
}

/* MOVE — source becomes blank, target becomes the source slot. */
export function moveMealSlot(plan, srcDay, srcSlotId, tgtDay, tgtSlotId) {
  if (srcDay === tgtDay && srcSlotId === tgtSlotId) return plan;
  const src = getMealSlotByDayAndSlot(plan, srcDay, srcSlotId);
  let next = setMealSlotByDayAndSlot(plan, tgtDay, tgtSlotId, src);
  next = setMealSlotByDayAndSlot(next, srcDay, srcSlotId, blankSlot());
  return next;
}

/* SWAP — exchange the two cells. */
export function swapMealSlots(plan, srcDay, srcSlotId, tgtDay, tgtSlotId) {
  if (srcDay === tgtDay && srcSlotId === tgtSlotId) return plan;
  const src = getMealSlotByDayAndSlot(plan, srcDay, srcSlotId);
  const tgt = getMealSlotByDayAndSlot(plan, tgtDay, tgtSlotId);
  let next = setMealSlotByDayAndSlot(plan, tgtDay, tgtSlotId, src);
  next = setMealSlotByDayAndSlot(next, srcDay, srcSlotId, tgt);
  return next;
}

/* DUPLICATE — source unchanged, target becomes a copy of the source. */
export function duplicateMealSlot(plan, srcDay, srcSlotId, tgtDay, tgtSlotId) {
  if (srcDay === tgtDay && srcSlotId === tgtSlotId) return plan;
  const src = getMealSlotByDayAndSlot(plan, srcDay, srcSlotId);
  return setMealSlotByDayAndSlot(plan, tgtDay, tgtSlotId, src);
}

/* CLEAR — set the cell to a blank slot. */
export function clearMealSlotInPlan(plan, day, slotId) {
  return setMealSlotByDayAndSlot(plan, day, slotId, blankSlot());
}

/* REPLACE — alias of moveMealSlot (target overwritten, source cleared). */
export function replaceMealSlot(plan, srcDay, srcSlotId, tgtDay, tgtSlotId) {
  return moveMealSlot(plan, srcDay, srcSlotId, tgtDay, tgtSlotId);
}

/* ════════════════════════════════════════════════════════════════════════
   RECIPE → MEAL SLOT HELPERS
   Used by the searchable recipe picker, drag-and-drop recipe cards, and
   the mobile "Add to meal plan" flow.
   ════════════════════════════════════════════════════════════════════════ */

/* Categories / tags considered a natural fit for each meal slot. Used to
   surface "Recommended for X" first — never to exclude other recipes. */
const SLOT_RECIPE_HINTS = {
  breakfast:    { categories: ["Breakfast", "Baking"],         tags: ["Smoothie", "Snack"] },
  morningTea:   { categories: ["Snack", "Baking", "Lunchbox"], tags: [] },
  lunch:        { categories: ["Lunch", "Lunchbox", "Side"],   tags: ["Salad", "Leftovers"] },
  afternoonTea: { categories: ["Snack", "Baking"],             tags: ["Smoothie"] },
  dinner:       { categories: ["Dinner"],                      tags: ["Slow Cooker", "Freezer Friendly", "One Pot"] },
  dessert:      { categories: ["Baking"],                      tags: ["Dessert"] },
};

export function recipeMatchesMealSlot(recipe, slotId) {
  if (!recipe) return false;
  const hint = SLOT_RECIPE_HINTS[slotId];
  if (!hint) return false;
  if (hint.categories.includes(recipe.category)) return true;
  const tags = recipe.tags || [];
  return tags.some(t => hint.tags.includes(t));
}

/* Group recipes into { recommended, others } given a meal slot id. */
export function getRecipeOptionsForMealSlot(recipes = [], slotId) {
  const recommended = [];
  const others = [];
  (recipes || []).forEach(r => {
    if (recipeMatchesMealSlot(r, slotId)) recommended.push(r);
    else others.push(r);
  });
  return { recommended, others };
}

/* Build a meal-slot object from a recipe. Optional opts:
     keepCook       — preserve an already-assigned cook on the target slot
     existingCook   — the existing cook id (used when keepCook is true)
     overrideTitle  — set a custom title (otherwise recipe title)            */
export function buildMealSlotFromRecipe(recipe, opts = {}) {
  if (!recipe) return blankSlot();
  const { keepCook = false, existingCook = null, overrideTitle = null } = opts;
  return {
    title: overrideTitle || recipe.title || "",
    recipeId: recipe.id || null,
    imageUrl: recipe.imageUrl || "",
    imageEmoji: recipe.imageEmoji || "",
    assignedCook: keepCook ? (existingCook || null) : (recipe.assignedDefaultCook || existingCook || null),
    status: "Planned",
    caloriesPerServe: recipe.caloriesPerServe ?? null,
    macros: {
      protein: recipe.macros?.protein ?? null,
      carbs:   recipe.macros?.carbs   ?? null,
      fat:     recipe.macros?.fat     ?? null,
    },
    servings: recipe.serves ?? 1,
    notes: "",
  };
}

/* Resolve a slot's nutrition from its linked recipe (per serve). */
export function getRecipeNutritionForMealSlot(slot, recipe) {
  return calculateMealNutrition(slot, recipe);
}

/* Apply a recipe to a slot in the plan. Honours keepCook so we don't blast
   the existing cook if the user wants to keep it. */
export function applyRecipeToMealSlot(plan, day, slotId, recipe, opts = {}) {
  const slotObj = buildMealSlotFromRecipe(recipe, opts);
  return setMealSlotByDayAndSlot(plan, day, slotId, slotObj);
}

/* Explicit replace alias to match the requested API surface. */
export function replaceMealSlotWithRecipe(plan, day, slotId, recipe, opts = {}) {
  return applyRecipeToMealSlot(plan, day, slotId, recipe, opts);
}
